import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    violet: Palette['primary'];
    neutral: Palette['primary'];
    green: Palette['primary'];
  }

  interface PaletteOptions {
    violet: PaletteOptions['primary'];
    neutral: PaletteOptions['primary'];
    green: PaletteOptions['primary'];
  }

  interface TypographyVariants {
    text: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    text: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    text: true;
    xl: true;
    md: true;
    sm: true;
    xxs: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    violet: true;
    neutral: true;
    green: true;
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    violet: true;
    neutral: true;
    green: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    grey: true;
  }
}

declare module '@mui/material/CircularProgress' {
  interface CircularProgressPropsColorOverrides {
    neutral: true;
  }
}

export const AppTheme = createTheme({
  palette: {
    primary: {
      main: '#4353FF',
      100: '#E6E9FF',
      200: '#BFC5FF',
      300: '#C7CEFF',
      400: '#7C8AFF',
      500: '#182CFF',
    },
    secondary: {
      main: '#E1E7EF',
      100: '#C5CCD6',
      200: '#a6b0bd',
      300: '#8C98A8',
      500: '#7B899C',
      700: '#606F84',
      800: '#536377',
      900: '#3B4C61',
    },
    text: {
      primary: 'rgba(44, 46, 51, 1)',
      secondary: 'rgba(111, 111, 111, 1)',
    },
    error: {
      main: '#FF355A',
      100: '#FFCED8',
      200: '#FF9AAD',
      300: '#FF6481',
      400: '#FF355A',
      500: '#FF1842',
      600: '#FF0236',
      700: '#E40028',
      800: '#CC0023',
      900: '#B3001B',
    },
    grey: {
      100: 'rgba(225, 231, 239, 1)',
      200: 'rgba(68, 68, 68, 1)',
      300: '#F9FAFC',
      800: '#232323',
      900: '#314053',
    },
    violet: {
      main: '#9747FF',
      light: '#D4B4FF',
      100: '#E7D6FB',
      200: '#CAAaf1',
      300: '#AC7CE8',
      400: '#9354E0',
      500: '#833BDB',
      600: '#7B2EDA',
      700: '#6921C2',
      800: '#5D1CAE',
      900: '#501599',
    },
    neutral: {
      main: '#FFFFFF',
      100: '#F8F8F8',
      200: '#EAEAEA',
      300: '#DCDCDC',
      400: '#CDCDCD',
      500: '#BFBFBF',
      600: '#B1B1B1',
      700: '#A3A3A3',
      800: '#959595',
      900: '#878787',
    },
    green: {
      main: '#EDFDEE',
      100: '#D9F8DA',
      200: '#ADF2B0',
      300: '#7FEC82',
      400: '#5BE65D',
      500: '#46E346',
      600: '#3BE13A',
      700: '#2FC82D',
      800: '#25B125',
      900: '#118116',
    },
  },
  typography: (palette) => ({
    fontFamily: 'Helvetica, Arial, sans-serif',
    h1: {
      fontSize: '2.5rem',
      lineHeight: '3rem',
      fontWeight: 'bold',
    },
    h2: {
      fontSize: '2rem',
      lineHeight: '2.5rem',
      fontWeight: 'bold',
    },
    h3: {
      fontSize: '1.5rem',
      lineHeight: '2rem',
      fontWeight: 'bold',
    },
    h4: {
      fontSize: '1.125rem',
      lineHeight: '1.5rem',
      fontWeight: 'bold',
    },
    h5: {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      fontWeight: 'bold',
      color: palette.secondary[900],
    },
    h6: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      fontWeight: 'bold',
    },
    text: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      fontWeight: 700,
      color: palette.primary.main,
    },
    xl: {
      fontSize: '1rem',
      lineHeight: '1.125rem',
      color: palette.secondary[900],
    },
    md: {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      color: palette.secondary[900],
    },
    sm: {
      fontSize: '0.75rem',
      lineHeight: '0.875rem',
      color: '#314053',
    },
    xxs: {
      fontSize: '0.625rem',
      lineHeight: '0.75rem',
      color: palette.secondary[900],
    },
  }),
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        '@media (max-width: 1280px)': {
          html: { fontSize: '90%' },
        },
        '&::-webkit-scrollbar': {
          width: '5px',
          height: '5px',
        },
        '&::-webkit-scrollbar-track': {
          background: theme.palette.secondary.main,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.primary.main,
          borderRadius: '4px',
          '&:hover': {
            background: theme.palette.primary[400],
          },
        },
      }),
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontSize: '0.875rem',
          lineHeight: '1rem',
          '&.MuiButton-textSecondary': {
            '&.Mui-disabled': {
              color: '#C5CCD6',
              opacity: 0.5,
            },
          },
          '&.MuiButton-outlinedPrimary:hover, &.MuiButton-outlinedPrimary:focus': {
            color: '#4353FF',
          },
        },
        sizeSmall: {
          padding: '0 20px',
          height: '32px',
          fontSize: '0.875rem',
          lineHeight: '1rem',
          fontWeight: 700,
        },
        ['colorGrey' as string]: {
          border: '1px solid #728196',
          color: '#3B4C61',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root, & .MuiInputLabel-root': {
            fontSize: '0.75rem',
            lineHeight: '0.875rem',
            color: '#3B4C61',
            fontWeight: 400,
            borderRadius: 8,
          },
          '& .MuiInputBase-input': {
            padding: '8px 12px',
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#C5CCD6',
            },
            '&:hover fieldset': {
              borderColor: '#A9B3FF',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#A9B3FF',
              borderWidth: '1px',
            },
            '& .MuiInputBase-inputMultiline': {
              padding: 0,
            },
          },
          '& .MuiInput-root.MuiInputBase-input::placeholder': {
            color: '#E1E7EF',
            opacity: 1,
          },
          '& .MuiInputBase-root.Mui-error': {
            borderColor: '#FF6481',
          },
          '& .MuiFormHelperText-root.Mui-error': {
            color: '#FF6481',
          },
          '& .MuiInputBase-root.MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before': {
            borderBottom: '1px solid #A9B3FF',
          },
          '& .MuiInput-underline:before': {
            borderBottom: '1px solid #EDF5FF',
          },
          '& .MuiInput-underline:after': {
            borderBottom: '1px solid #EDF5FF',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 'bold',
          lineHeight: '0.875rem',
          color: '#536377',
          paddingLeft: '8px',
          '&.MuiFormLabel-colorError': {
            color: '#FF6481',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          height: '40px',
          padding: '0 20px',
          '&.Mui-disabled': {
            color: '#C5CCD6',
            opacity: 0.5,
          },
          '&.MuiButton-text': {
            '&.MuiButton-sizeSmall': {
              '--width': 'unset',
              padding: '0 4px',
            },
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: '#F8F8F8',
          borderRadius: '8px',
          border: '1px solid #E1E7EF',
          '& th': {
            padding: '12px',
            background: 'unset',
            fontSize: '0.875rem',
            lineHeight: '1rem',
            fontWeight: 700,
            color: '#536377',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          padding: '4px 8px',
          height: 'unset',
          borderRadius: '32px',
          fontSize: '0.625rem',
          fontWeight: 700,
          lineHeight: '0.75rem',
          '&.MuiTableCell-body .MuiChip-colorViolet': {
            border: '1px solid #D4B4FF',
            color: '#9747FF',
          },
        },
        label: {
          padding: '0',
        },
        colorPrimary: {
          border: '1px solid #4353FF',
          '&.MuiChip-outlined': {
            color: '#4353FF',
            border: '1px solid #BFC5FF',
          },
        },
        colorSecondary: {
          border: '1px solid #C5CCD6',
          color: '#3B4C61',
        },
        ['colorViolet' as string]: {
          border: '1px solid #D4B4FF',
          color: '#9747FF',
        },
        ['colorGreen' as string]: {
          border: '1px solid #5BE65D',
          color: '#118116',
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.MuiPaginationItem-rounded': {
            borderRadius: '8px',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          lineHeight: '1rem',
          height: '32px',
          color: '#606F84',
          borderRadius: '8px',
        },
        multiple: {
          fontSize: '0.75rem',
          lineHeight: '0.875rem',
          color: '#606F84',
          padding: '7px 12px',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          marginLeft: '8px',
          fontSize: '0.75rem',
          lineHeight: '0.875rem',
          fontWeight: 700,
          color: '#536377',
          marginBottom: '4px',
          '&.MuiFormLabel-colorError': {
            color: '#FF6481',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          minHeight: '32px',
          '& fieldset': {
            borderColor: '#C5CCD6',
            borderRadius: '8px',
          },
          '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#A9B3FF',
          },
          '&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#A9B3FF',
            borderWidth: '1px',
          },
          '& .MuiSelect-icon': {
            width: '20px',
            height: '20px',
            top: 'calc(50% - .8em)',
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        ['colorViolet' as string]: {
          backgroundColor: '#9747FF',
          color: '#FFFFFF',
        },
        ['colorGreen' as string]: {
          backgroundColor: '#2FC82D',
          color: '#FFFFFF',
        },
        ['colorNeutral' as string]: {
          backgroundColor: '#A3A3A3',
          color: '#000000',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        sizeSmall: ({ theme }) => ({
          width: 28,
          height: 16,
          padding: 0,
          display: 'flex',
          '&:active': {
            '& .MuiSwitch-thumb': {
              width: 15,
            },
            '& .MuiSwitch-switchBase.Mui-checked': {
              transform: 'translateX(9px)',
            },
          },
          '& .MuiSwitch-switchBase': {
            height: 'unset',
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(12px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.primary.main,
              },
            },
          },
          '& .MuiSwitch-thumb': {
            boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
            width: 12,
            height: 12,
            borderRadius: 6,
            transition: theme.transitions.create(['width'], {
              duration: 200,
            }),
          },
          '& .MuiSwitch-track': {
            borderRadius: 16 / 2,
            opacity: 1,
            backgroundColor: 'rgba(0,0,0,.25)',
            boxSizing: 'border-box',
          },
          '& .Mui-disabled.MuiSwitch-switchBase + .MuiSwitch-track': {
            opacity: 0.5,
          },
        }),
      },
    },
  },
});

export const a11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`,
});
